import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react';

interface CollaboratorSelectorProps {
  collaborators: any[];
  selectedCollaborator: any;
  onSelect: (collaborator: any) => void;
}

const CollaboratorSelector: React.FC<CollaboratorSelectorProps> = ({
  collaborators,
  selectedCollaborator,
  onSelect
}) => {
  const { t } = useTranslation();

  // Ne rien afficher si un seul collaborateur (sélection automatique)
  if (!collaborators || collaborators.length <= 1) {
    return null;
  }

  // Helper pour obtenir l'ID du collaborateur (peut être 'id' ou 'collaboratorId')
  const getCollaboratorId = (collaborator: any) => {
    return collaborator?.collaboratorId || collaborator?.id;
  };

  // Helper pour vérifier si un collaborateur est sélectionné
  const isSelected = (collaborator: any) => {
    const collabId = getCollaboratorId(collaborator);
    const selectedId = getCollaboratorId(selectedCollaborator);
    return collabId === selectedId;
  };

  return (
    <div className="space-y-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
      <div className="flex items-center mb-2">
        <div className="mr-3 p-2 bg-primary/10 rounded-full">
          <Users className="h-5 w-5 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800">
          {t('booking.selectCollaborator', 'Choisissez un professionnel')}
        </h3>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {collaborators.map((collaborator) => {
          const collaboratorId = getCollaboratorId(collaborator);
          const selected = isSelected(collaborator);

          return (
            <button
              key={collaboratorId}
              onClick={() => onSelect(collaborator)}
              className={`
                p-4 rounded-xl border-2 transition-all text-left
                ${
                  selected
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-primary/50 bg-white'
                }
              `}
            >
            <div className="flex items-center">
              {collaborator.avatar ? (
                <img
                  src={collaborator.avatar}
                  alt={`${collaborator.firstName} ${collaborator.lastName}`}
                  className="w-12 h-12 rounded-full object-cover mr-3"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                  <span className="text-primary font-semibold text-lg">
                    {collaborator.firstName?.charAt(0)}{collaborator.lastName?.charAt(0)}
                  </span>
                </div>
              )}
              <div className="flex-1">
                <div className="font-medium text-gray-900">
                  {collaborator.firstName} {collaborator.lastName}
                </div>
                {collaborator.profession && (
                  <div className="text-sm text-gray-500">
                    {collaborator.profession}
                  </div>
                )}
              </div>
              {selected && (
                <div className="ml-3">
                  <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          </button>
          );
        })}
      </div>
    </div>
  );
};

export default CollaboratorSelector;
